import React, { FC, FormEvent, useCallback, useMemo } from 'react';
import { Prompt, useHistory } from 'react-router';

import { EvSection, EvSpinner } from '@evinced-private/ui-common';

import { RoutesHelper } from 'src/helpers/RoutesHelper';

import { useNavigationBlocking } from './hooks/useNavigationBlocking';
import { usePropertySave } from './hooks/usePropertySave';
import { usePropertySettingsEditor } from './hooks/usePropertySettingsEditor';
import { usePropertySourceMode } from './hooks/usePropertySourceMode';
import { useRouteRegistration } from './hooks/useRouteRegistration';
import { PropertySettingsEditorProps } from './types/PropertySettingsEditorProps';
import { PropertySettingsContextValue, PropertySettingsProvider } from './context';
import {
	ActionsSectionModule,
	FileTypeSectionModule,
	GroupingSectionModule,
	LoginSectionModule,
	SchedulingSectionModule,
	ScopeSectionModule,
	SeedsSectionModule,
	TitleSectionModule
} from './sections';
import { setLoading } from './state';

export const PropertySettingsEditor: FC<PropertySettingsEditorProps> = ({
	initialProperty,
	sourceMode: initialSourceMode,
	isNew,
	propertyId
}) => {
	const history = useHistory();

	const { state, dispatch, isDirty, isListMode } = usePropertySettingsEditor({
		initialProperty,
		sourceMode: initialSourceMode,
		isNew
	});

	const { shouldBlockNavigation, setShouldBlockNavigation } = useNavigationBlocking(isDirty);
	useRouteRegistration(propertyId);

	const setSourceMode = usePropertySourceMode({ dispatch });

	const goToProperty = useCallback(
		(targetPropertyId: string): void => {
			dispatch(setLoading(true));
			setShouldBlockNavigation(false);
			history.push(RoutesHelper.getPropertyPagePath(targetPropertyId));
		},
		[dispatch, setShouldBlockNavigation, history]
	);

	const save = usePropertySave({
		propertyId,
		dispatch,
		onSaved: goToProperty
	});

	const handleSubmit = useCallback(
		(e: FormEvent<HTMLFormElement>): void => {
			e.preventDefault();
			if (state.draft) {
				save({ draft: state.draft, isListMode });
			}
		},
		[save, state.draft, isListMode]
	);

	const contextValue: PropertySettingsContextValue = useMemo(
		() => ({
			state,
			dispatch,
			isDirty,
			isListMode,
			propertyId,
			save,
			setSourceMode,
			goToProperty
		}),
		[state, dispatch, isDirty, isListMode, propertyId, save, setSourceMode, goToProperty]
	);

	if (state.isLoading || !state.draft) {
		return (
			<div className="spinner">
				<EvSpinner />
			</div>
		);
	}

	return (
		<PropertySettingsProvider value={contextValue}>
			<form className="property-settings-page" onSubmit={handleSubmit} noValidate>
				<Prompt
					when={shouldBlockNavigation}
					message="You have unsaved changes, are you sure you want to leave?"
				/>
				<TitleSectionModule />
				<EvSection
					className="property-settings-content"
					ariaLabel={isListMode ? 'Property list settings' : 'Property crawl settings'}
					skipLinkId={isListMode ? 'list-settings' : 'crawl-settings'}
				>
					<SeedsSectionModule />
					{!isListMode && (
						<>
							<ScopeSectionModule />
							<FileTypeSectionModule />
							<GroupingSectionModule />
						</>
					)}
					<LoginSectionModule />
					<SchedulingSectionModule />
				</EvSection>
				<ActionsSectionModule />
			</form>
		</PropertySettingsProvider>
	);
};
